// Pure parsing/verification helpers for the clinic text templates (`ClinicalTextTemplate.text`).
// No Angular, no HTTP -- consumed by `ClinicalReportRepositoryImpl` to build the template prompt
// and to sanity-check Gemini's answer.
//
// Two shapes of template exist in the wild and both must work. Roughly half of a real clinic's
// templates use the `[[...]]` gap convention; the other half are SKELETONS -- section headings and
// empty list items with no gap markers at all. Everything below is written so that "no placeholder"
// is a first-class shape rather than the degenerate case it used to be.

import { ClinicalTemplateWarning } from '../models/clinical-report.model';
import { decodeHtmlEntities, htmlToPlainText, toComparableText } from './clinical-html.util';

export interface TemplateOptionsPlaceholder {
  readonly raw: string;
  readonly kind: 'options';
  readonly options: readonly string[];
}

export interface TemplateFreeTextPlaceholder {
  readonly raw: string;
  readonly kind: 'free';
  readonly hint: string;
}

// `[[ ]]` / `[[]]`: the single most common gap in the clinic's own templates, and the one carrying
// no instruction whatsoever. Kept apart from `free` because a free gap has a hint to follow and a
// blank one has nothing -- the only guidance available is the text around it, which is why
// `describePlaceholders` reports each gap with its neighbouring words.
export interface TemplateBlankPlaceholder {
  readonly raw: string;
  readonly kind: 'blank';
}

export type TemplatePlaceholder =
  | TemplateOptionsPlaceholder
  | TemplateFreeTextPlaceholder
  | TemplateBlankPlaceholder;

const PLACEHOLDER_PATTERN = /\[\[(.*?)\]\]/g;
// Same pattern without a capture group -- `String.split` interleaves a regex's captured groups
// into the result, which would otherwise make placeholder hints show up as "fixed fragments" too.
const PLACEHOLDER_SPLIT_PATTERN = /\[\[.*?\]\]/g;

// Both separators occur, and `|` is in fact the more common of the two -- splitting only on `/`
// silently demoted every `|` list to a free-text hint, letting the model write anything it liked
// where the clinic had spelled out a closed set of answers. `|` wins when both appear, because a
// `/` inside an option ("Grave DH >10% / IRC") is far likelier than the reverse.
function splitOptions(content: string): readonly string[] {
  const separator = content.includes('|') ? '|' : '/';
  return content.split(separator).map((option) => option.trim()).filter((option) => option.length > 0);
}

// Gap content is authored inside a rich-text editor, so it carries entities (`&nbsp;`, `&lt;`) and
// occasionally inline markup. Decoding before classifying is what makes
// `[[Normal&nbsp; /&nbsp; Ligera DH 6%]]` parse into the two options a human sees.
function readContent(rawContent: string): string {
  return decodeHtmlEntities(rawContent.replace(/<[^>]*>/g, ''));
}

export function parsePlaceholders(templateText: string): readonly TemplatePlaceholder[] {
  const placeholders: TemplatePlaceholder[] = [];

  for (const match of templateText.matchAll(PLACEHOLDER_PATTERN)) {
    const raw = match[0];
    const content = readContent(match[1]);

    if (content.trim().length === 0) {
      placeholders.push({ raw, kind: 'blank' });
    } else if (content.includes('|') || content.includes('/')) {
      placeholders.push({ raw, kind: 'options', options: splitOptions(content) });
    } else {
      placeholders.push({ raw, kind: 'free', hint: content.trim() });
    }
  }

  return placeholders;
}

// Its own non-global copy on purpose: `RegExp.test` on a `/g` regex advances `lastIndex`, and
// `String.matchAll` starts from wherever `lastIndex` was left -- so testing with the shared pattern
// made the very next `parsePlaceholders` skip the gaps this had just found.
const HAS_PLACEHOLDER_PATTERN = /\[\[.*?\]\]/;

export function hasPlaceholders(templateText: string): boolean {
  return HAS_PLACEHOLDER_PATTERN.test(templateText);
}

export type TemplateSegment =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'placeholder'; readonly placeholder: TemplatePlaceholder };

// The same template read as an ordered sequence instead of a placeholder list, so a preview can
// render the clinic's fixed wording and its gaps distinctly without re-implementing the pattern.
export function splitTemplateSegments(templateText: string): readonly TemplateSegment[] {
  const segments: TemplateSegment[] = [];
  let cursor = 0;

  for (const match of templateText.matchAll(PLACEHOLDER_PATTERN)) {
    const start = match.index ?? 0;
    if (start > cursor) segments.push({ kind: 'text', text: templateText.slice(cursor, start) });
    segments.push({ kind: 'placeholder', placeholder: parsePlaceholders(match[0])[0] });
    cursor = start + match[0].length;
  }

  if (cursor < templateText.length) segments.push({ kind: 'text', text: templateText.slice(cursor) });

  return segments;
}

// Enough words to identify the cell label a gap belongs to ("Grado de hidratación"), short enough
// that the map stays readable when a template has 118 of them.
const CONTEXT_BEFORE_CHARS = 70;
const CONTEXT_AFTER_CHARS = 25;

function contextAround(segments: readonly TemplateSegment[], index: number): { before: string; after: string } {
  const before = segments
    .slice(0, index)
    .map((segment) => (segment.kind === 'text' ? htmlToPlainText(segment.text) : ' ▮ '))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trimEnd()
    .slice(-CONTEXT_BEFORE_CHARS);

  const next = segments[index + 1];
  const after = (next?.kind === 'text' ? htmlToPlainText(next.text) : '')
    .replace(/\s+/g, ' ')
    .trimStart()
    .slice(0, CONTEXT_AFTER_CHARS);

  return { before, after };
}

function describeGap(placeholder: TemplatePlaceholder): string {
  switch (placeholder.kind) {
    case 'options':
      return `elige UNA opción entre: ${placeholder.options.join(' | ')}`;
    case 'free':
      return `texto libre, pista: "${placeholder.hint}"`;
    case 'blank':
      return 'hueco sin pista: deduce qué va aquí por la etiqueta que lo precede y por la unidad que lo sigue';
  }
}

// The `{{huecos}}` value when the template uses the gap convention: one numbered entry per gap with
// the words around it, because 4 out of every 10 gaps in a real clinic's templates are blank and
// their surrounding cell label is the ONLY thing that says what belongs there.
function describeGaps(templateText: string): string {
  const segments = splitTemplateSegments(templateText);
  const lines: string[] = [];
  let ordinal = 0;

  segments.forEach((segment, index) => {
    if (segment.kind !== 'placeholder') return;
    ordinal += 1;
    const { before, after } = contextAround(segments, index);
    const context = `contexto: "…${before} ▮ ${after}…"`;
    lines.push(`${ordinal}. ${segment.placeholder.raw} -> ${describeGap(segment.placeholder)} (${context})`);
  });

  return [
    `La plantilla tiene ${ordinal} hueco(s) "[[...]]". Rellena SOLO esos huecos:`,
    ...lines,
  ].join('\n');
}

const SECTION_WITH_LABEL = /^(.{1,80}?):\s*(.*)$/;
// A parenthetical right after a heading ("ANAMNESIS: ? (reseña, dieta, entorno)") is the clinic
// telling the vet what belongs in that section -- the skeleton equivalent of a gap hint.
const TRAILING_HINT = /^\((.+)\)$/;
// The clinic writes "?" (or a bare dash) where a value is expected, so those are placeholders for
// "nothing here yet" rather than content to preserve -- and they can precede a parenthetical hint,
// as in "ANAMNESIS: ? (reseña, dieta, entorno)".
const EMPTY_MARKERS = /^[?\-·_\s]+/;

// The `{{huecos}}` value when the template has NO gap markers: the model still needs an explicit
// task list, and "(sin huecos)" told it there was nothing to do -- which is precisely why a
// skeleton template used to come back echoed or empty.
function describeSections(templateText: string): string {
  const lines = htmlToPlainText(templateText)
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const described = lines.map((line, index) => {
    const isItem = line.startsWith('-');
    const body = isItem ? line.slice(1).trim() : line;

    // An empty bullet or a lone dash ("<li><br></li>", "<p>-&nbsp;</p>") is the clinic leaving room
    // for a line the vet is meant to write. Labelling it by its own marker would tell the model to
    // write a section called "-".
    if (body.replace(EMPTY_MARKERS, '').trim().length === 0) {
      return `${index + 1}. punto vacío -> escribe aquí el contenido que corresponda`;
    }

    const match = SECTION_WITH_LABEL.exec(body);
    const label = match ? match[1].trim() : body;
    const rest = match ? match[2].trim() : '';
    const prefix = `${index + 1}. ${isItem ? 'ítem' : 'sección'} "${label}"`;
    const content = rest.replace(EMPTY_MARKERS, '').trim();

    if (content.length === 0) return `${prefix} -> sin contenido, escríbelo`;

    const hint = TRAILING_HINT.exec(content);
    if (hint) return `${prefix} -> sin contenido; la plantilla pide: ${hint[1]}`;

    return `${prefix} -> ya trae texto ("${content}"): consérvalo y complétalo si procede`;
  });

  return [
    'La plantilla NO tiene huecos "[[...]]": es un esqueleto de secciones y debes escribir el',
    'contenido de cada una a partir de los datos de la consulta. Estructura detectada:',
    ...described,
  ].join('\n');
}

export function describePlaceholders(templateText: string): string {
  return hasPlaceholders(templateText) ? describeGaps(templateText) : describeSections(templateText);
}

// Fixed text this short is too generic (punctuation, a lone connector word) to reliably prove the
// model preserved the clinic's wording -- checking it would just produce noisy false positives.
// Measured on the VISIBLE text, so a fragment that is mostly `<td style="width: 18.69%;">` no
// longer clears the bar on the strength of its markup.
const MIN_FIXED_FRAGMENT_LENGTH = 15;

// Structured descriptors rather than pre-formatted strings -- detects the three failure modes
// below and lets the presentation layer decide how to translate/surface each one.
//
// Every comparison runs on `toComparableText`, never on the raw strings: template and answer are
// both HTML, and comparing markup to markup reported an "alteration" for every reflowed attribute
// and an "invalid option" for every `&nbsp;` the clinic had typed inside a choice list.
export function verifyFilledTemplate(
  templateText: string,
  generatedText: string,
): readonly ClinicalTemplateWarning[] {
  const warnings: ClinicalTemplateWarning[] = [];
  const placeholders = parsePlaceholders(templateText);

  for (const placeholder of generatedText.match(PLACEHOLDER_PATTERN) ?? []) {
    warnings.push({ kind: 'unfilled-placeholder', placeholder });
  }

  // A skeleton template is MEANT to be written over -- every heading it carries would otherwise be
  // reported as fixed text the model altered, which is the whole of the noise a vet used to see.
  if (placeholders.length === 0) return warnings;

  const comparableAnswer = toComparableText(generatedText);

  for (const placeholder of placeholders) {
    if (placeholder.kind !== 'options') continue;
    const matchedOption = placeholder.options.some((option) => {
      const comparableOption = toComparableText(option);
      return comparableOption.length > 0 && comparableAnswer.includes(comparableOption);
    });
    if (!matchedOption) {
      warnings.push({ kind: 'invalid-option', placeholder: placeholder.raw, options: placeholder.options });
    }
  }

  for (const fragment of templateText.split(PLACEHOLDER_SPLIT_PATTERN)) {
    const visible = htmlToPlainText(fragment).trim();
    if (visible.length < MIN_FIXED_FRAGMENT_LENGTH) continue;
    if (!comparableAnswer.includes(toComparableText(fragment))) {
      // The visible text, not the raw HTML slice: this is rendered to the vet, and a fragment that
      // starts mid-`<td>` reads as corruption rather than as their own wording.
      warnings.push({ kind: 'altered-fixed-text', fragment: visible });
    }
  }

  return warnings;
}
